import { useCallback } from "react";

import { useLocalStorage } from "./useLocalStorage";
import { ExtendedViewItem } from "@/types/receipt";
import { ACCOUNTING_WIDGET_TYPE } from "@/constants/popup";

const ACTIVE_WIDGET_KEY = "zeipt_active_accounting_widget_id";
const EMAIL_MAP_KEY = "zeipt_email_by_widget_id";

const EMPTY_EMAIL_MAP: Record<string, string> = {};

export interface UseActiveAccountingWidget {
  activeWidgetId: string | null;
  getStoredEmail: (settingsId: string) => string | null;
  recordSuccessfulSend: (settingsId: string, email: string) => void;
  onAccountingSendSuccess: (
    item: ExtendedViewItem
  ) => (email: string) => void;
}

export function useActiveAccountingWidget(): UseActiveAccountingWidget {
  const [activeWidgetId, setActiveWidgetId] = useLocalStorage<string | null>(
    ACTIVE_WIDGET_KEY,
    null
  );
  const [emailByWidgetId, setEmailByWidgetId] = useLocalStorage<
    Record<string, string>
  >(EMAIL_MAP_KEY, EMPTY_EMAIL_MAP);

  const getStoredEmail = useCallback(
    (settingsId: string) => emailByWidgetId[settingsId] ?? null,
    [emailByWidgetId]
  );

  const recordSuccessfulSend = useCallback(
    (settingsId: string, email: string) => {
      const emailUnchanged = emailByWidgetId[settingsId] === email;
      const activeUnchanged = activeWidgetId === settingsId;
      if (emailUnchanged && activeUnchanged) return;

      if (!activeUnchanged) setActiveWidgetId(settingsId);
      if (!emailUnchanged) {
        setEmailByWidgetId((prev) => ({ ...prev, [settingsId]: email }));
      }
    },
    [
      activeWidgetId,
      emailByWidgetId,
      setActiveWidgetId,
      setEmailByWidgetId,
    ]
  );

  const onAccountingSendSuccess = useCallback(
    (item: ExtendedViewItem) => (email: string) => {
      if (item.extended_view_position === ACCOUNTING_WIDGET_TYPE) {
        recordSuccessfulSend(item.receipt_view_service_settings_id, email);
      }
    },
    [recordSuccessfulSend]
  );

  return {
    activeWidgetId,
    getStoredEmail,
    recordSuccessfulSend,
    onAccountingSendSuccess,
  };
}
